import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CountryContactsModel, CountryContactsResourceService } from '../vn-api';

/** A business error of the backend: `HTTP 570 {message: 'country.contacts.*', data}`; `key` is empty when the answer is not one of them. */
export interface CountryContactsError {
  key: string;
  data?: Record<string, unknown>;
  status: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function countryContactsErrorOf(err: unknown): CountryContactsError | null {
  if (!(err instanceof HttpErrorResponse)) {
    return null;
  }
  const body: unknown = err.error;
  const message = isRecord(body) && typeof body['message'] === 'string' ? body['message'] : '';
  const data = isRecord(body) && isRecord(body['data']) ? body['data'] : undefined;
  return {key: message, data, status: err.status};
}

/** How the number is written in a country: calling code, then the groups of the rest. The first matching row wins. */
const WHATSAPP_PATTERNS: readonly { code: string; length: number; groups: readonly number[] }[] = [
  {code: '7', length: 11, groups: [3, 3, 2, 2]},
  {code: '998', length: 12, groups: [2, 3, 2, 2]},
  {code: '992', length: 12, groups: [2, 3, 2, 2]},
  {code: '996', length: 12, groups: [3, 3, 3]},
  {code: '1', length: 11, groups: [3, 3, 4]},
];

/**
 * The saved number (digits only, no plus - as the backend keeps it) as people write it: `77715253995` -> `+7 771 525 39 95`.
 * A number of a country the screen does not know is shown as `+` and the digits. Empty stays empty.
 */
export function formatWhatsapp(digits: string | null | undefined): string {
  const value = (digits ?? '').trim();
  if (!value) {
    return '';
  }
  const pattern = /^\d+$/.test(value) ? WHATSAPP_PATTERNS.find(p => value.length === p.length && value.startsWith(p.code)) : undefined;
  if (!pattern) {
    return `+${value}`;
  }
  const parts: string[] = [pattern.code];
  let at = pattern.code.length;
  for (const size of pattern.groups) {
    parts.push(value.substring(at, at + size));
    at += size;
  }
  return `+${parts.join(' ')}`;
}

/**
 * The contacts of the countries the site shows (today: «WhatsApp оператора» for weight and tiered cakes), kept per country in `app_properties`.
 * Reading is for every signed-in employee, writing is for the admin (the backend decides, `GET /adm/catalog/me` tells a screen who is the admin).
 */
@Injectable({
  providedIn: 'root'
})
export class HxCountryContactsService {
  constructor(
    private resource: CountryContactsResourceService,
  ) {
  }

  /** Every country, with or without a number, in the order of `countryId`. */
  getContacts(): Promise<CountryContactsModel[]> {
    return firstValueFrom(this.resource.getCountryContacts());
  }

  /**
   * Writes the number as typed («+7 771 525 39 95» is fine); an empty text clears it. The answer carries what is stored now - digits only.
   * The backend refuses a wrong number with `570 country.contacts.operatorWhatsappInvalid {field, min, max}`.
   */
  saveOperatorWhatsapp(countryId: number, typed: string | null | undefined): Promise<CountryContactsModel> {
    return firstValueFrom(this.resource.saveCountryContacts(countryId, {operatorWhatsapp: (typed ?? '').trim()}));
  }
}
